import CreatableSelect from 'react-select/creatable';
import { useMemo } from 'react';
import useStore from '../lib/store';
import { groupLocationsByCoords } from '../lib/locationSummaries';
import { titleCaseLocation } from '../lib/utils';

const MAX_LOCATION_OPTIONS = 50;

export function LocationSelect({
    value,
    onChange,
}: {
    value: string;
    onChange: (val: string) => void;
}) {
    const locations = useStore(state => state.map.locations);
    const groupedLocations = useMemo(() => (
        groupLocationsByCoords(locations?.values())
            .sort((a, b) => b.rentalCount - a.rentalCount || a.name.localeCompare(b.name))
    ), [locations]);
    const locationOptions = useMemo(() => {
        const options = groupedLocations
            .slice(0, MAX_LOCATION_OPTIONS)
            .map((location) => ({
                value: location.name,
                label: `${titleCaseLocation(location.name)} (${location.rentalCount})`,
                sortKey: location.key,
            }));

        const selectedGroup = groupedLocations.find(location => (
            location.aliases.some(alias => alias.name.toLowerCase() === value.toLowerCase())
        ));

        if (value && selectedGroup && !options.some(option => option.value.toLowerCase() === selectedGroup.name.toLowerCase())) {
            options.unshift({
                value: selectedGroup.name,
                label: `${titleCaseLocation(selectedGroup.name)} (${selectedGroup.rentalCount})`,
                sortKey: selectedGroup.key,
            });
        } else if (value && !selectedGroup && !options.some(option => option.value.toLowerCase() === value.toLowerCase())) {
            options.unshift({ value, label: titleCaseLocation(value), sortKey: value.toLowerCase() });
        }

        return options;
    }, [groupedLocations, value]);

    const selectedGroup = value
        ? groupedLocations.find(location => (
            location.aliases.some(alias => alias.name.toLowerCase() === value.toLowerCase())
        ))
        : null;
    const selected = value
        ? selectedGroup
            ? {
                value: selectedGroup.name,
                label: `${titleCaseLocation(selectedGroup.name)} (${selectedGroup.rentalCount})`,
                sortKey: selectedGroup.key,
            }
            : { value, label: titleCaseLocation(value), sortKey: value.toLowerCase() }
        : null;

    return (
        <CreatableSelect
            isClearable
            options={locationOptions}
            value={selected}
            onChange={(opt) => onChange(opt?.value || '')}
            formatCreateLabel={(inputValue) => `Search: "${inputValue}"`}
            placeholder="Location"
            className="text-sm"
            classNamePrefix="select"
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 50 }) }}
        />
    );
}
